import { computed } from "vue"
import { useWindowSize } from "@vueuse/core"

type MenuViewportProfile = {
  buttonSize: number
  closedHeight: number
  hoverHeight: number
  openSmallWidth: number
}

const MENU_BREAKPOINT = 640
const OPEN_MARGIN = 32

// Shared spacing tokens used by both MenuContainer and Menu.
const COLLAPSED_BUTTON_PADDING_X = 4
const COLLAPSED_BUTTON_PADDING_TOP = 4
const COLLAPSED_BUTTON_PADDING_BOTTOM = 6
const OPEN_BUTTON_PADDING_X = 10
const OPEN_BUTTON_PADDING_TOP = 10
const OPEN_BUTTON_PADDING_BOTTOM = 10
// Content-open (open-large) gets its own padding so it can differ from the
// nav-open (open-small) box. Tune independently of the values above.
const OPEN_LARGE_BUTTON_PADDING_X = 8
const OPEN_LARGE_BUTTON_PADDING_TOP = 8
const OPEN_LARGE_BUTTON_PADDING_BOTTOM = 8
const OPEN_LETTERFORM_SCALE = 4

const DESKTOP_PROFILE: MenuViewportProfile = {
  buttonSize: 55,
  closedHeight: 68,
  hoverHeight: 80,
  openSmallWidth: 550,
}

const MOBILE_PROFILE: MenuViewportProfile = {
  buttonSize: 32,
  closedHeight: 44,
  hoverHeight: 44,
  openSmallWidth: 0,
}

export function useMenuGeometry() {
  const { width: windowWidth, height: windowHeight } = useWindowSize()

  const isMobile = computed(() => windowWidth.value < MENU_BREAKPOINT)
  const profile = computed(() =>
    isMobile.value ? MOBILE_PROFILE : DESKTOP_PROFILE,
  )

  const containerWidth = computed(() => profile.value.buttonSize * 5)

  const collapsedHeight = (state: "closed" | "hovering") => {
    const p = profile.value
    return state === "hovering" ? p.hoverHeight : p.closedHeight
  }

  const toggleHeight = computed(() => profile.value.closedHeight)

  const openLargeWidth = computed(() => windowWidth.value - OPEN_MARGIN)
  const openSmallWidth = computed(() =>
    isMobile.value ? openLargeWidth.value : profile.value.openSmallWidth,
  )
  const openHeight = computed(() => windowHeight.value - OPEN_MARGIN)

  const collapsedButtonPaddingY =
    COLLAPSED_BUTTON_PADDING_TOP + COLLAPSED_BUTTON_PADDING_BOTTOM
  const openButtonPaddingY =
    OPEN_BUTTON_PADDING_TOP + OPEN_BUTTON_PADDING_BOTTOM

  const letterformClosedHeight = () =>
    collapsedHeight("closed") - collapsedButtonPaddingY
  const letterformHoverHeight = () =>
    collapsedHeight("hovering") - collapsedButtonPaddingY
  const letterformOpenHeight = computed(
    () =>
      (profile.value.closedHeight - openButtonPaddingY) * OPEN_LETTERFORM_SCALE,
  )

  return {
    windowWidth,
    windowHeight,
    isMobile,
    breakpoint: MENU_BREAKPOINT,
    containerWidth,
    toggleHeight,
    buttonPadding: {
      collapsed: {
        x: COLLAPSED_BUTTON_PADDING_X,
        top: COLLAPSED_BUTTON_PADDING_TOP,
        bottom: COLLAPSED_BUTTON_PADDING_BOTTOM,
      },
      open: {
        x: OPEN_BUTTON_PADDING_X,
        top: OPEN_BUTTON_PADDING_TOP,
        bottom: OPEN_BUTTON_PADDING_BOTTOM,
      },
      openLarge: {
        x: OPEN_LARGE_BUTTON_PADDING_X,
        top: OPEN_LARGE_BUTTON_PADDING_TOP,
        bottom: OPEN_LARGE_BUTTON_PADDING_BOTTOM,
      },
    },
    openSmallWidth,
    openLargeWidth,
    openHeight,
    collapsedHeight,
    letterform: {
      closedHeight: letterformClosedHeight,
      hoverHeight: letterformHoverHeight,
      openHeight: letterformOpenHeight,
    },
  }
}
